import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import type {LinkResolverFn} from 'prismicio'
import type {
  ButtonDocument,
  StresstimonialsCardSliceTwoCardsPrimaryCardsWithDrawersItem,
  StresstimonialsCardSliceTwoCardsPrimaryDrawerBioInfoLeftCardItem,
} from 'prismicio-types'
import Drawer from 'src/common/Drawer'
import {getButtonsData} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {PlusButton} from 'src/common/PlusButton'
import {StresstimonialsDrawerContent} from 'src/common/StresstimonialsDrawerContent'

type SingleCardProps = {
  card: StresstimonialsCardSliceTwoCardsPrimaryCardsWithDrawersItem
  cardIndex: number
  isActive: boolean
  onOpen: () => void
  onClose: () => void
  bioInfo: StresstimonialsCardSliceTwoCardsPrimaryDrawerBioInfoLeftCardItem[]
  linkResolver: LinkResolverFn
  componentIndex: number
  pageUid: string
  buttonsData?: ButtonDocument[]
  componentName: string
}

export const SingleCard = ({
  card,
  cardIndex,
  isActive,
  onOpen,
  onClose,
  bioInfo,
  linkResolver,
  componentIndex,
  pageUid,
  buttonsData,
  componentName,
}: SingleCardProps): JSX.Element => {
  const domainCountry = useGetDomainCountry()

  const {primaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: card.cta_button,
  })

  const hasAdditionalImage = isFilled.image(card.additional_image)

  return (
    <>
      <div
        className={cn('flex items-start gap-12 md:gap-20', {
          'flex-1 justify-between md:flex-auto md:justify-start': hasAdditionalImage,
        })}
      >
        {/* Additional Image (Left side for left card) */}
        {hasAdditionalImage && cardIndex === 0 && (
          <div className="flex w-[146px] shrink-0 items-start justify-center md:w-[216px]">
            <PrismicNextImage field={card.additional_image} className="h-auto max-w-full" />
          </div>
        )}

        {/* Main Card */}
        <div
          className="group flex w-[147px] max-w-[216px] cursor-pointer flex-col gap-4 md:w-full"
          onClick={(e) => {
            e.stopPropagation()

            trackMossAnalytics({
              event: 'interaction',
              interaction: {
                element: `StresstimonialsCard-${isFilled.keyText(card.drawer_title) ? card.drawer_title : 'unknown'}`,
                elementText: 'card',
                location: `stresstimonials-card-block`,
                type: 'click',
              },
              page: {
                domainCountry,
                type: 'stresstimonials',
              },
            })

            onOpen()
          }}
        >
          {isFilled.keyText(card.eyebrow) && (
            <div className="flex flex-col gap-2.5">
              <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.66px] text-moss-grey-400">{card.eyebrow}</p>
              <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
            </div>
          )}

          {isFilled.image(card.image) && (
            <div className="relative h-[200px] w-full overflow-hidden rounded-lg bg-moss-grey-100 ring-0 transition-all duration-300 group-hover:ring-2 group-hover:ring-green-neon md:h-[288px]">
              <PrismicNextImage
                field={card.image}
                className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <PlusButton className="absolute bottom-2.5 right-2.5" />
            </div>
          )}

          {isFilled.keyText(card.bottom_comment) && (
            <div className="flex flex-col gap-3.5">
              <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
              <p className="text-18 font-semibold uppercase leading-normal tracking-[0.96px] text-moss-black">
                {card.bottom_comment}
              </p>
              <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
            </div>
          )}
        </div>

        {/* Additional Image (Right side for right card) */}
        {hasAdditionalImage && cardIndex === 1 && (
          <div className="flex w-[146px] shrink-0 items-start justify-center md:w-[216px]">
            <PrismicNextImage field={card.additional_image} className="h-auto max-w-full" />
          </div>
        )}
      </div>

      {/* Drawer */}
      <Drawer isOpen={isActive} setIsOpen={onClose}>
        <StresstimonialsDrawerContent
          video_source={card.drawer_video_source}
          fallback_image={card.drawer_video_fallback_image}
          drawer_title={card.drawer_title}
          drawer_bio_title={card.drawer_bio_title}
          bio_info={bioInfo}
          free_text={card.free_text}
          drawer_image={card.drawer_image}
          free_text_2={card.free_text_2}
          cta_eyebrow={card.cta_eyebrow}
          cta_title={card.cta_title}
          cta_description={card.cta_description}
          primaryButtonData={primaryButtonData}
          linkResolver={linkResolver}
          componentIndex={componentIndex}
          pageUid={pageUid}
          componentName={componentName}
        />
      </Drawer>
    </>
  )
}
